/* eslint-disable no-useless-catch */
import endpoints from '../../constants/endpoints'
import { httpAuth } from '../../config/apiClient'
import keys from '../../constants/keys'
import { getFileExtension, handleError } from '../../utils'
import { logoutHandler } from './auth'
import { createDriveActionPopWithJob } from './pops'
import {
  DriveAccessLevel,
  DriveContextType,
  DriveNodeType,
  IDriveAccessEntry,
  IDriveNode,
  IDrivePermissions,
  IDriveUploadUrl,
  IDriveViewHistoryEntry,
  IDriveViewStats,
  isViewableInline,
} from '../../config/driveTypes'
import { appendIdentityParams, isExternalFormLink } from '../../utils/jotformLink'

export const actionTypes = {
  SET_ROOTS: '[DRIVE] SET_ROOTS',
  SET_ROOTS_LOADING: '[DRIVE] SET_ROOTS_LOADING',
  ADD_ROOT: '[DRIVE] ADD_ROOT',
  REMOVE_ROOT: '[DRIVE] REMOVE_ROOT',
  SET_CONTENTS_LOADING: '[DRIVE] SET_CONTENTS_LOADING',
  SET_FOLDER_CONTENTS: '[DRIVE] SET_FOLDER_CONTENTS',
  ADD_ITEM: '[DRIVE] ADD_ITEM',
  REMOVE_ITEM: '[DRIVE] REMOVE_ITEM',
  MARK_ITEM_VIEWED: '[DRIVE] MARK_ITEM_VIEWED',
  RENAME_NODE: '[DRIVE] RENAME_NODE',
  UPDATE_ITEM_META: '[DRIVE] UPDATE_ITEM_META',
  SET_SEARCH_INDEX_LOADING: '[DRIVE] SET_SEARCH_INDEX_LOADING',
  SET_SEARCH_INDEX: '[DRIVE] SET_SEARCH_INDEX',
}

// Cached search snapshot; refetch when older than this so edits made elsewhere eventually surface.
const SEARCH_INDEX_TTL_MS = 2 * 60 * 1000

// `retail_id_node_id` is `{retail_id}#{node_id}`; path params and parent_id use the node_id.
export const getNodeId = (node: IDriveNode) => node.retail_id_node_id.split('#')[1] || node.retail_id_node_id

const onApiError = (err: any, dispatch: any) => {
  const status = err?.response?.status
  if (status) {
    handleError(status, () => dispatch(logoutHandler()))
  }
}

export const handleFetchRoots = () => async (dispatch: any) => {
  try {
    dispatch({ type: actionTypes.SET_ROOTS_LOADING, isLoading: true })
    const { data } = await httpAuth.get(endpoints.driveRoots)
    dispatch({ type: actionTypes.SET_ROOTS, roots: data?.roots || [] })
  } catch (err) {
    onApiError(err, dispatch)
  } finally {
    dispatch({ type: actionTypes.SET_ROOTS_LOADING, isLoading: false })
  }
}

// Lazily fetch the flat search index (whole accessible tree).
// Idempotent: skips if a fetch is in flight or the cached snapshot is still fresh, so callers can dispatch freely.
export const handleFetchSearchIndex = () => async (dispatch: any, getState: any) => {
  const { searchIndex, searchIndexLoading, searchIndexLoadedAt } = getState().drive
  const isFresh = !!searchIndex && !!searchIndexLoadedAt && Date.now() - searchIndexLoadedAt < SEARCH_INDEX_TTL_MS
  if (searchIndexLoading || isFresh) return
  try {
    dispatch({ type: actionTypes.SET_SEARCH_INDEX_LOADING, isLoading: true })
    const { data } = await httpAuth.get(endpoints.driveNodes())
    dispatch({
      type: actionTypes.SET_SEARCH_INDEX,
      items: data?.items || [],
      truncated: !!data?.truncated,
      loadedAt: Date.now(),
    })
  } catch (err) {
    dispatch({ type: actionTypes.SET_SEARCH_INDEX_LOADING, isLoading: false })
    onApiError(err, dispatch)
  }
}

export const handleFetchFolderContents =
  (folderId: string, { append = false }: { append?: boolean } = {}) =>
  async (dispatch: any, getState: any) => {
    try {
      const existing = getState().drive.contentsByFolderId[folderId]
      const cursor = append ? existing?.nextCursor || '' : ''

      dispatch({ type: actionTypes.SET_CONTENTS_LOADING, folderId, isLoading: true })
      const { data } = await httpAuth.get(endpoints.driveFolderContents(folderId, 50, cursor))

      dispatch({
        type: actionTypes.SET_FOLDER_CONTENTS,
        folderId,
        items: data?.items || [],
        nextCursor: data?.next_cursor || null,
        // Top-level `name` on the response is this folder's own name (its children don't carry it).
        folderName: data?.name ?? null,
        append,
      })
    } catch (err) {
      dispatch({ type: actionTypes.SET_CONTENTS_LOADING, folderId, isLoading: false })
      onApiError(err, dispatch)
    }
  }

export const handleCreateRootFolder =
  ({ name, permissions, icon }: { name: string; permissions: IDrivePermissions; icon?: string }) =>
  async (dispatch: any) => {
    try {
      // Folder fields are nested under `data`; `parent_id` omitted = root folder.
      const folderData: { name: string; icon?: string } = { name }
      if (icon) folderData.icon = icon
      const { data } = await httpAuth.post(endpoints.driveFolders, { data: folderData, permissions })
      if (data?.folder) {
        // The create response omits access_level; stamp OWNER optimistically so the creator sees the
        // access chip + edit/add actions immediately on entering the folder (they own what they created).
        const folder: IDriveNode = {
          ...data.folder,
          access_level: data.folder.access_level ?? DriveAccessLevel.OWNER,
        }
        dispatch({ type: actionTypes.ADD_ROOT, root: folder })
      }
      return data?.folder as IDriveNode
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Sub-folders inherit the root's access, so no `permissions` are sent; the new folder joins its parent's contents.
export const handleCreateSubFolder =
  ({ parentId, name, icon }: { parentId: string; name: string; icon?: string }) =>
  async (dispatch: any) => {
    try {
      const folderData: { name: string; icon?: string } = { name }
      if (icon) folderData.icon = icon
      const { data } = await httpAuth.post(endpoints.driveFolders, { parent_id: parentId, data: folderData })
      if (data?.folder) {
        dispatch({ type: actionTypes.ADD_ITEM, folderId: parentId, item: data.folder })
      }
      return data?.folder as IDriveNode
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// `icon` is optional: pass a URL to set it, an empty string to clear it, or undefined to leave it unchanged.
export const handleUpdateFolder =
  (folderId: string, { name, icon }: { name?: string; icon?: string | null }) =>
  async (dispatch: any) => {
    try {
      const folderData: { name?: string; icon?: string | null } = {}
      if (name !== undefined) folderData.name = name
      if (icon !== undefined) folderData.icon = icon || null
      const { data } = await httpAuth.put(endpoints.driveFolder(folderId), { data: folderData })
      if (data?.folder) {
        dispatch({ type: actionTypes.RENAME_NODE, node: data.folder })
      }
      return data?.folder as IDriveNode
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Pass parentId for a sub-folder (removes it from the parent's contents); omit for a root folder.
export const handleDeleteFolder = (folderId: string, parentId?: string) => async (dispatch: any) => {
  try {
    await httpAuth.delete(endpoints.driveFolder(folderId))
    if (parentId) {
      dispatch({ type: actionTypes.REMOVE_ITEM, folderId: parentId, fileId: folderId })
    } else {
      dispatch({ type: actionTypes.REMOVE_ROOT, folderId })
    }
  } catch (err) {
    onApiError(err, dispatch)
    throw err
  }
}

export const handleCreateLink =
  ({ parentId, name, url }: { parentId: string; name: string; url: string }) =>
  async (dispatch: any) => {
    try {
      const { data } = await httpAuth.post(endpoints.driveFiles, {
        parent_id: parentId,
        name,
        node_type: DriveNodeType.LINK,
        data: { url },
      })
      if (data?.item) {
        dispatch({ type: actionTypes.ADD_ITEM, folderId: parentId, item: data.item })
      }
      return data?.item as IDriveNode
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Three-step upload: presign → direct S3 POST → create metadata record.
export const handleUploadFile =
  ({ parentId, file }: { parentId: string; file: File }) =>
  async (dispatch: any) => {
    try {
      // Step 1 — presigned URL (also runs the WRITE check). Server derives Content-Type + max size from the
      // extension, so we send only parent_id + name.
      const { data: presign } = await httpAuth.post<IDriveUploadUrl>(endpoints.driveUploadUrl, {
        parent_id: parentId,
        name: file.name,
      })

      // Step 2 — direct multipart POST to S3 with fetch mode 'no-cors' (matching the media uploader): the bucket
      // has no CORS policy, so a normal request would be blocked. Echo every presign field verbatim.
      const formData = new FormData()
      Object.entries(presign.fields || {}).forEach(([key, value]) => formData.append(key, value as string))
      formData.append('file', file)
      await fetch(presign.upload_url, { method: 'POST', body: formData, mode: 'no-cors' })

      // Step 3 — create the metadata record.
      const { data } = await httpAuth.post(endpoints.driveFiles, {
        parent_id: parentId,
        name: file.name,
        node_type: DriveNodeType.FILE,
        data: {
          s3_key: presign.s3_key,
          size_bytes: file.size,
          mime_type: file.type || 'application/octet-stream',
        },
      })
      if (data?.item) {
        dispatch({ type: actionTypes.ADD_ITEM, folderId: parentId, item: data.item })
      }
      return data?.item as IDriveNode
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

export const handleDeleteItem = (fileId: string, folderId: string) => async (dispatch: any) => {
  try {
    await httpAuth.delete(endpoints.driveFile(fileId))
    dispatch({ type: actionTypes.REMOVE_ITEM, folderId, fileId })
  } catch (err) {
    onApiError(err, dispatch)
    throw err
  }
}

// Rename a file/link (and, for links, its url). `name` is top-level on PUT /files/{id}; `url` goes under
// `data` — unlike /folders, this endpoint doesn't accept an icon. parent_id isn't patchable here (move endpoint).
export const handleUpdateItem =
  (fileId: string, folderId: string, { name, url }: { name?: string; url?: string }) =>
  async (dispatch: any) => {
    try {
      const payload: { name?: string; data?: { url: string } } = {}
      if (name !== undefined) payload.name = name
      if (url !== undefined) payload.data = { url }
      const { data } = await httpAuth.put(endpoints.driveFile(fileId), payload)
      if (data?.item) {
        dispatch({ type: actionTypes.UPDATE_ITEM_META, folderId, fileId, node: data.item })
      }
      return data?.item as IDriveNode
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Marks an item viewed in a folder's contents bucket so the "unviewed" dot clears immediately (optimistic).
export const markItemViewed = (folderId: string, fileId: string) => ({
  type: actionTypes.MARK_ITEM_VIEWED,
  folderId,
  fileId,
  viewedAt: Math.floor(Date.now() / 1000),
})

// Files: fetch a presigned download URL then open it. Links: open the url directly.
// Pass folderId (when opened from a folder's contents) to optimistically mark the item viewed.
// Open a LINK node in a new tab. Links have no download URL, so record the view here (files are recorded
// server-side by the download-url call). A URL without a scheme (e.g. "www.example.com") would resolve
// relative to the app origin, so prepend https:// to open it as an absolute external URL.
export const handleOpenLink = (node: IDriveNode, folderId?: string) => async (dispatch: any, getState: any) => {
  try {
    dispatch(handleRecordView(getNodeId(node)) as any)
    if (node.url) {
      let href = /^[a-z][a-z0-9+.-]*:\/\//i.test(node.url) ? node.url : `https://${node.url}`
      // Jotform forms (forms.storee.ai), and any link explicitly flagged "external", read caller-identity
      // off the query string to attribute the submission — attach it here so every such link dropped into
      // the Library carries it, same rule Tasks' external-link subtasks use (see utils/jotformLink.ts).
      if (isExternalFormLink(href)) {
        const { first_name, last_name, default_group, groups } = getState().config.config
        const uid = getState().auth.uid
        href = appendIdentityParams(href, { first_name, last_name, default_group, groups, user_id: uid })
      }
      window.open(href, '_blank', 'noopener,noreferrer')
    }
    if (folderId) dispatch(markItemViewed(folderId, getNodeId(node)))
  } catch (err) {
    onApiError(err, dispatch)
    throw err
  }
}

// Fetch a presigned url for opening a FILE in a new tab: inline for browser-viewable types and Office docs
// (opened via the Office web viewer), attachment otherwise. Records the view; returns the url (or null) so
// the caller can cache it (see driveUrlCache) and reopen without re-hitting the server.
export const handleFetchOpenUrl =
  (node: IDriveNode, folderId?: string) =>
  async (dispatch: any): Promise<string | null> => {
    try {
      const isOfficeDoc = keys.extensionsForCloudViewer.includes(getFileExtension(node.name || '').toLowerCase())
      const disposition = isViewableInline(node.mime_type) || isOfficeDoc ? 'inline' : 'attachment'
      const { data } = await httpAuth.get(endpoints.driveDownloadUrl(getNodeId(node)), { params: { disposition } })
      if (folderId) dispatch(markItemViewed(folderId, getNodeId(node)))
      return data?.download_url || null
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Fetch a presigned INLINE url for a media file (image/video) so it can be shown in the in-app
// lightbox instead of a new browser tab. The download-url call records the view server-side; we also
// optimistically mark it viewed. Returns the url (or null) — no window.open here.
export const handleFetchInlineUrl =
  (node: IDriveNode, folderId?: string) =>
  async (dispatch: any): Promise<string | null> => {
    try {
      const { data } = await httpAuth.get(endpoints.driveDownloadUrl(getNodeId(node)), {
        params: { disposition: 'inline' },
      })
      if (folderId) dispatch(markItemViewed(folderId, getNodeId(node)))
      return data?.download_url || null
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Force a download regardless of type — the explicit "Download" action, even for inline-viewable files.
export const handleDownloadItem = (node: IDriveNode) => async (dispatch: any) => {
  try {
    const { data } = await httpAuth.get(endpoints.driveDownloadUrl(getNodeId(node)), {
      params: { disposition: 'attachment' },
    })
    if (data?.download_url) {
      // Anchor-click (not a new tab) so the attachment downloads without navigating away or leaving a blank tab.
      const link = document.createElement('a')
      link.href = data.download_url
      link.rel = 'noopener'
      document.body.appendChild(link)
      link.click()
      link.remove()
    }
  } catch (err) {
    onApiError(err, dispatch)
    throw err
  }
}

// ─── Views ────────────────────────────────────────────────────────────────────
// Records a view (READ access). Call ONLY for links — file views are recorded by the download-url call, so
// recording here too would double-count. Best-effort: failures are swallowed so tracking never blocks the user.
export const handleRecordView = (nodeId: string) => async () => {
  try {
    await httpAuth.post(endpoints.driveRecordView(nodeId))
  } catch {
    // best-effort — ignore
  }
}

// Aggregate view counts for an item (READ access). Returns zeros if the item has no views yet.
export const handleFetchViewStats =
  (nodeId: string) =>
  async (dispatch: any): Promise<IDriveViewStats> => {
    try {
      const { data } = await httpAuth.get(endpoints.driveViewStats(nodeId))
      return {
        total_views: data?.views?.total_views || 0,
        distinct_viewers: data?.views?.distinct_viewers || 0,
      }
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Per-user view history (ADMIN access), cursor-paginated. Reveals who accessed the item.
export const handleFetchViewHistory =
  (nodeId: string, { cursor = '' }: { cursor?: string } = {}) =>
  async (dispatch: any): Promise<{ entries: IDriveViewHistoryEntry[]; nextCursor: string | null }> => {
    try {
      const { data } = await httpAuth.get(endpoints.driveViewHistory(nodeId, 50, cursor))
      return {
        entries: (data?.view_history || []) as IDriveViewHistoryEntry[],
        nextCursor: data?.next_cursor || null,
      }
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// ─── Access management ────────────────────────────────────────────────────────
// Only root nodes carry direct grants; sub-nodes inherit and return an empty list.
// grant/revoke propagate asynchronously (SQS) — callers should update the UI optimistically.

// GET returns context_type as an int (0/1/2); tolerate a string form too, just in case.
const normalizeContextType = (value: any): DriveContextType => {
  if (typeof value === 'string') {
    const byName: { [k: string]: DriveContextType } = {
      user: DriveContextType.USER,
      group: DriveContextType.GROUP,
      tag: DriveContextType.TAG,
    }
    return byName[value.toLowerCase()] ?? DriveContextType.USER
  }
  return value as DriveContextType
}

export const handleFetchNodeAccess =
  (nodeId: string) =>
  async (dispatch: any): Promise<IDriveAccessEntry[]> => {
    try {
      const { data } = await httpAuth.get(endpoints.driveAccess, { params: { node_id: nodeId } })
      return (data?.access || []).map((entry: any) => ({
        context_id: entry.context_id,
        context_type: normalizeContextType(entry.context_type),
        access_level: entry.access_level as DriveAccessLevel,
      }))
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

// Fire-and-forget: after an upload batch, notify the folder's audience with ONE DRIVE_FOLDER ActionPop
// (not one per file) whose CTA opens the folder. `title` is the FOLDER name; the "new files in this folder"
// message comes from the handler's descriptionLocaleKey. Audience = grants on `audienceSourceId` (the folder's
// root ancestor, or the folder itself when it's a root), then narrowed to the caller's `audienceScope` inside
// `createDriveActionPopWithJob` (pop API rejects out-of-tree recipients with 403). Never throws — must not
// affect the upload flow.
export const announceFolderUpload =
  ({ folderId, folderName, audienceSourceId }: { folderId: string; folderName: string; audienceSourceId: string }) =>
  async (dispatch: any) => {
    try {
      if (!folderId || !audienceSourceId) return
      const entries: IDriveAccessEntry[] = await dispatch(handleFetchNodeAccess(audienceSourceId))
      await dispatch(
        createDriveActionPopWithJob({
          folderName,
          folderId,
          groups: entries.filter((e) => e.context_type === DriveContextType.GROUP).map((e) => e.context_id),
          users: entries.filter((e) => e.context_type === DriveContextType.USER).map((e) => e.context_id),
          tags: entries.filter((e) => e.context_type === DriveContextType.TAG).map((e) => e.context_id),
        }) as any
      )
    } catch {
      // best-effort — swallow
    }
  }

export const handleGrantAccess = (nodeId: string, contexts: IDriveAccessEntry[]) => async (dispatch: any) => {
  try {
    await httpAuth.post(endpoints.driveAccess, { node_id: nodeId, contexts })
  } catch (err) {
    onApiError(err, dispatch)
    throw err
  }
}

export const handleUpdateAccessLevel =
  (nodeId: string, contextId: string, accessLevel: DriveAccessLevel) => async (dispatch: any) => {
    try {
      await httpAuth.put(endpoints.driveAccess, {
        node_id: nodeId,
        context_id: contextId,
        access_level: accessLevel,
      })
    } catch (err) {
      onApiError(err, dispatch)
      throw err
    }
  }

export const handleRevokeAccess = (nodeId: string, contextIds: string[]) => async (dispatch: any) => {
  try {
    await httpAuth.delete(endpoints.driveAccess, { data: { node_id: nodeId, contexts_ids: contextIds } })
  } catch (err) {
    onApiError(err, dispatch)
    throw err
  }
}
